import React, { useMemo, useState } from 'react'
import { Autocomplete, TextField, Box, Chip, Typography, Alert, CircularProgress } from '@mui/material'
import { useOncotreeSearch } from 'src/api/oncotree'

/**
 * Tumour-type picker for the somatic step of the analysis wizard.
 *
 * WHY THIS IS REQUIRED
 * ---------------------
 * OncoKB evidence levels are per-indication. Dabrafenib against BRAF V600E is
 * LEVEL_1 in melanoma and LEVEL_3B in colorectal cancer, so without a code the
 * levels that come back cannot be attributed to this patient. The somatic
 * ranking knows this and withholds its therapeutic-level term entirely on an
 * untyped analysis, which means the actionability column loses its top signal.
 *
 * Required since roadmap R1.19: the wizard does not submit a somatic analysis
 * without a tumour type, and the backend refuses one it cannot find in the
 * OncoTree table. A diagnosis that is not settled yet is not a blocker: the
 * type can be changed later from the somatic view, with a reason, and only the
 * OncoKB answers and the ranking are re-run.
 */
const OncotreeSelect = ({ value, onChange, disabled = false, required = true, showError = false }) => {
  const [inputValue, setInputValue] = useState('')

  // The query is the typed text, not the selected label: once a value is chosen
  // MUI sets inputValue to its name, and re-searching on that would collapse the
  // dropdown to the single already-selected node.
  const query = useMemo(() => (value && inputValue === value.name ? '' : inputValue), [inputValue, value])

  const { data: options = [], isFetching } = useOncotreeSearch(query, { limit: 30 })

  return (
    <Box>
      <Autocomplete
        disabled={disabled}
        value={value || null}
        onChange={(_e, next) => onChange(next || null)}
        inputValue={inputValue}
        onInputChange={(_e, next) => setInputValue(next)}
        options={options}
        loading={isFetching}
        // The server already ranks (exact code, then code prefix, then name
        // prefix, then contains, then hierarchy depth). Re-filtering client-side
        // would drop matches found on tissue or main_type, which is how "skin"
        // finds the skin nodes at all.
        filterOptions={(x) => x}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, val) => option?.oncotree_code === val?.oncotree_code}
        noOptionsText={query ? 'No matching tumour type' : 'Start typing to search'}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.oncotree_code}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Chip label={option.oncotree_code} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap>
                  {option.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {[option.tissue, option.main_type].filter(Boolean).join(' • ')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tumour type (OncoTree)"
            placeholder="Search by name, code or tissue, e.g. melanoma, LUAD, skin"
            required={required}
            error={required && showError && !value}
            helperText={
              required && showError && !value
                ? 'A tumour type is required for a somatic analysis'
                : 'Determines which OncoKB evidence levels apply to this patient'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {required && !value && (
        <Alert severity="info" sx={{ mt: 1 }}>
          A somatic analysis needs its tumour type: OncoKB resolves therapeutic evidence per indication. If the
          diagnosis is not final, pick the closest type; it can be changed later from the somatic view.
        </Alert>
      )}
    </Box>
  )
}

export default OncotreeSelect
